import { useCallback, useEffect, useMemo, useState } from "react";
import { PageHeader } from "@features/page-header/page-header";
import {
    Box,
    Button,
    Grid,
    ListItemButton,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableRow,
    Typography,
} from "@mzc-pdc/ui";
import { PageTools } from "@features/page-tools/page-tools";
import SearchTextField from "@features/list-search/search-textfield";
import {
    IconAdd,
    IconDownload,
    IconMoreHoriz,
    IconRemove,
    IconStar,
    IconStarOutline,
    ImageEmpty,
} from "@mzc-cloudplex/icons";
import { Trans, useTranslation } from "react-i18next";
import withSearchQueryParamsContext from "@components/params/withSearchQueryParamsContext";
import { useDispatch, useSelector } from "react-redux";
import {
    getLinkedAssets,
    increaseLinkedAssetsDownloadCount,
    updateCollection,
    updateCollectionAction,
} from "@modules/collection";
import LinkedAssetsColumn, {
    LinkedAssetsColumnV2,
} from "@routes/collections/ui/columns-linked-assets-detail-collection";
import { Menu } from "@components_v2/menu";
import theme from "@styles/theme";
import TableList from "@components_v2/table-list/table-list";
import ListTools from "@features/list-tools/list-tools";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { CONSTANTS, TABLE_LIST_TYPE } from "@constants";
import { Pagination } from "@components_v2/pgaination/pagination";
import { useHistory } from "react-router-dom";
import { togglePropertyPanel } from "@modules/propertyPanel";
import menuService from "@services/menuService";
import useFilter from "@hooks/useFilter";
import { createDownloadJob, getDownloadJobs } from "@modules/downloadJobs";
import { setNotification } from "@modules/notification";
import DownloadInfoMessage from "@routes/share/ui/DownloadInfoMessage";
import compareVersions, {
    VERSION_CM_13766_SHARES_V3,
    VERSION_CM_14205_ASSET_STARRED,
    VERSION_CM_14143_REQUEST,
    VERSION_CM_3963_ADD_ASSET_TYPE,
    VERSION_CM_9213_ASSET_MULTI_EXTERNAL_SHARE,
    VERSION_CM_9660_BULK_ARCHIVE_RESTORE,
    VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
} from "@cores/version";
import { getAssetTypeSelectList, listAssetExtensions, listAssetTypes } from "@modules/assets";
import Loadable from "@react-loadable/revised";
import CardLinkedAsset from "@routes/collections/card-linked-asset";
import ListCardView from "@components_v2/table-list/list-card-view";
import moment from "moment/moment";
import Skeleton from "@components_v2/skeleton/skeleton";
import DialogDelete from "@features/dialog/dialog-delete";
import useSettings from "@hooks/useSettings";
import { saveViewInfo } from "@cores/viewStorage";
import * as yup from "yup";
import VersionedComponent from "@components/VersionedComponent";
import ReactDOM from "react-dom";
import DialogShare from "@features/dialog/share/dialog-share";
import DialogArchive from "@features/dialog/dialog-archive";
import { updateAsset } from "@modules/asset";
import { listJobs } from "@modules/bulkJobs";
import useArchive from "@hooks/useArchive";
import useCheckPermission from "@hooks/useCheckPermission";
import SkeletonLinkedAssetsCollectionTable from "@routes/collections/ui/table-skeleton-linked-assets-collection";
import { Popover } from "@mui/material";
import SelectFolderPath from "@components/modals/SelectFolderPath";
import DialogAddLinkedAssetsV2 from "@routes/asset/ui/detail/dialog-add-linked-assets-v2";
import { useLocation } from "react-router";
import countDaysBetweenDates from "@cores/countDaysBetweentDates";
import dayjs from "dayjs";
import { bulkAddFavoriteAPI, bulkRemoveFavoriteAPI } from "@modules/apis/favorite";
import { siteMode } from "@cores/siteMode";
import { DialogRequest, DialogRequestDownload } from "@routes/request/dialog-request";
import useDownloadJobs from "@hooks/useDownloadJobs";
import useAbortController from "@hooks/useAbortController";
import { canNavigateToArchiveDetail, isArchiveRelatedStatus } from "@utils/archive-utils";

const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const LoadableRenameModal = Loadable({
    loader: () => import("@components/modals/Rename"),
    loading: () => null,
});

const LoadableShareRequestModal = Loadable({
    loader: () => import("@components/modals/ShareRequestModal"),
    loading: () => null,
});

const LinkedAssetsDetailCollection = ({ collectionId, searchQueryParams }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const FILTER_LIST = [{ id: "mediaType", label: null, title: "Media type", value: null }];
    const isCms = siteMode.get() === "CMS";

    const { filterValue } = useFilter("collection");
    const { isCardView, updateIsCardView, resourceSettings, getSettingData } = useSettings({
        type: "General",
        domain: "collectionLinkedAssets",
    });
    const { getSettingData: getSettingMenuName, resourceSettings: resourceSettingMenuName } = useSettings({});
    const { generateDownloadJobHash, requestDownloadJob } = useDownloadJobs();
    const { getNewController, abortAll, handleApiResult } = useAbortController();

    const { requestArchive } = useArchive();
    const COLLECTIONS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const collection = useSelector(({ collection }) => collection);
    const linkedAssets = useSelector(({ collection }) => collection.data?.linkedAssets);
    const project = useSelector(({ project }) => project);
    const stage = useSelector(({ stage }) => stage);
    const assets = useSelector(({ assets }) => assets);
    const myProjectPermissions = useSelector(({ roleManagementV2 }) => roleManagementV2?.projectPermissions?.data);

    const [selectedItems, setSelectedItems] = useState([]);
    const [clickedItem, setClickedItem] = useState(null);
    const [offset, setOffset] = useState(0);
    const [modal, setModal] = useState(false);
    const [actionTarget, setActionTarget] = useState(null);
    const [actionPending, setActionPending] = useState({});
    const [tokenPaginationInfo, setTokenPaginationInfo] = useState({ currentPage: 0, data: [] });
    const [isMountPending, setIsMountPending] = useState(true);
    const [searchOptionValues, setSearchOptionValues] = useState({
        status: CONSTANTS("ACTIVE_STATUS"),
        mediaType: CONSTANTS("ASSET_SEARCH_MEDIA_TYPES"),
        ingestType: CONSTANTS("ASSET_INGEST_TYPES"),
    });
    const [showCreatePopover, setShowCreatePopover] = useState(null);

    const onHandleClickCheck = (item, value) => {
        if (value) setSelectedItems([...selectedItems, item]);
        else setSelectedItems(selectedItems.filter(({ id }) => id !== item.id));
    };

    const onToggleModal = (key, value, actionTarget = null) => {
        if (value) {
            setModal(key);
            setActionTarget(actionTarget ? [actionTarget] : null);
        } else {
            setModal("");
            setActionTarget(null);
            setSelectedItems([]);
        }
    };

    const onHandleClickDownload = async (asset) => {
        try {
            const key = asset.id;
            const downloadRequestTargets = {
                resourceId: asset?.id,
                projectId: project.id,
                resourceName: asset?.name,
            };

            await requestDownloadJob("ASSET", downloadRequestTargets, 1, false, project.id, key);
            dispatch(increaseLinkedAssetsDownloadCount([asset]));
        } catch (error) {
            console.log(error);
        }
    };

    const onHandleClickMultiDownload = async () => {
        if (!selectedItems?.length) return;

        try {
            const key = generateDownloadJobHash(selectedItems.map((item) => item.id));

            const downloadRequestTargets = {
                resourceId: selectedItems.length === 1 ? selectedItems[0].id : undefined,
                resourceIds: selectedItems.length > 1 ? selectedItems.map((item) => item.id) : undefined,
                projectId: project.id,
                resourceName: selectedItems[0].name,
            };

            await requestDownloadJob("ASSET", downloadRequestTargets, selectedItems.length, false, project.id, key);
            setSelectedItems([]);
            dispatch(increaseLinkedAssetsDownloadCount(selectedItems));
        } catch (error) {
            console.log(error);
        }
    };

    const listToolsButtons = useCallback(
        (isResponsive) => {
            const checkDisabledByOwnerLock = selectedItems.some(
                (item) => item.isOwnerLocked && item.created?.by?.id !== stage?.user?.id,
            );
            const checkDisabledDownload = selectedItems.some((item) => {
                const isExpired = item?.shareInfo?.expiresAt
                    ? countDaysBetweenDates({ toDate: dayjs(item?.shareInfo?.expiresAt) }) < 0
                    : false;

                const isExceedDownloadCount =
                    item?.shareInfo?.maxDownloads > 0 &&
                    item?.shareInfo?.maxDownloads <= item?.shareInfo?.downloadCount;

                const hasDownloadPermissionByShare = item?.shareInfo && !(isExpired || isExceedDownloadCount);

                return (
                    (!item?.shareInfo || !hasDownloadPermissionByShare) &&
                    (!DOWNLOAD_PERMISSIONS.DOWNLOAD || (item.isOwnerLocked && item.created?.by?.id !== stage?.user?.id))
                );
            });

            return [
                <TooltipButton
                    key="download"
                    circled
                    title={t("common::label::Download", "Download")}
                    size={"small"}
                    onClickButton={() => onHandleClickMultiDownload()}
                    buttonIcon={<IconDownload size={16} />}
                    buttonText={t("common::label::Download", "Download")}
                    isDivided={false}
                    isResponsive={isResponsive}
                    disabled={
                        selectedItems.filter((item) => isArchiveRelatedStatus(item.archiveStatus)).length > 0 ||
                        checkDisabledDownload
                    }
                />,
                <VersionedComponent key="request" version={VERSION_CM_13766_SHARES_V3}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Request`, `Request`)}
                        buttonText={t(`common::label::Request`, `Request`)}
                        size={"small"}
                        onClickButton={() => {
                            onToggleModal("request", true);
                        }}
                        buttonIcon={<IconDownload size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        hidden={!isCms} //TODO: 요청할 수 있도록 추가 작업이 필요함
                    />
                </VersionedComponent>,
                <VersionedComponent key="starred" version={VERSION_CM_14205_ASSET_STARRED}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Starred`, `Starred`)}
                        buttonText={t(`common::label::Starred`, `Starred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                false,
                            );
                        }}
                        buttonIcon={<IconStar size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!COLLECTIONS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => !item.myFavorite)}
                    />
                </VersionedComponent>,
                <VersionedComponent key="unstarred" version={VERSION_CM_14205_ASSET_STARRED}>
                    <TooltipButton
                        circled
                        title={t(`common::label::Unstarred`, `Unstarred`)}
                        buttonText={t(`common::label::Unstarred`, `Unstarred`)}
                        size={"small"}
                        onClickButton={() => {
                            onHandleClickStarred(
                                selectedItems.map((item) => item.id),
                                true,
                            );
                        }}
                        buttonIcon={<IconStarOutline size={16} />}
                        isDivided={false}
                        isResponsive={isResponsive}
                        disabled={!COLLECTIONS_PERMISSIONS.READ}
                        hidden={!isCms || !selectedItems.some((item) => item.myFavorite)}
                    />
                </VersionedComponent>,
                <TooltipButton
                    key="remove"
                    circled
                    title={t("common::label::Remove", "Remove")}
                    size={"small"}
                    onClickButton={() => onToggleModal("delete", true)}
                    buttonIcon={<IconRemove size={16} />}
                    buttonText={t("common::label::Remove", "Remove")}
                    isResponsive={isResponsive}
                    disabled={!COLLECTIONS_PERMISSIONS.EDIT}
                />,
            ];
        },
        [selectedItems, COLLECTIONS_PERMISSIONS, SHARES_PERMISSIONS, DOWNLOAD_PERMISSIONS, onHandleClickMultiDownload],
    );

    const getListToolsPopperButtons = useCallback(() => {
        const checkDisabledByOwnerLock = selectedItems.some(
            (item) => item.isOwnerLocked && item.created?.by?.id !== stage?.user?.id,
        );

        return [
            [
                <ListItemButton
                    key="share-link"
                    onClick={() => {
                        onToggleModal("shareLink", true);
                    }}
                    disabled={
                        !SHARES_PERMISSIONS.EXTERNAL ||
                        selectedItems.filter((item) => isArchiveRelatedStatus(item.archiveStatus)).length > 0 ||
                        checkDisabledByOwnerLock
                    }
                    hidden={compareVersions(stage.version, VERSION_CM_9213_ASSET_MULTI_EXTERNAL_SHARE) < 0 || isCms}
                >
                    <ListItemText primary={t("common::label::Share link", "Share link")} />
                </ListItemButton>,
                <ListItemButton
                    key="archive"
                    onClick={() => {
                        onToggleModal("archive", true);
                    }}
                    disabled={
                        !COLLECTIONS_PERMISSIONS.ARCHIVE ||
                        selectedItems.filter((item) => isArchiveRelatedStatus(item.archiveStatus)).length > 0 ||
                        checkDisabledByOwnerLock
                    }
                    hidden={compareVersions(stage.version, VERSION_CM_9660_BULK_ARCHIVE_RESTORE) < 0 || isCms}
                >
                    <ListItemText primary={t("common::label::Archive", "Archive")} />
                </ListItemButton>,
                <ListItemButton
                    key="request"
                    onClick={() => {
                        onToggleModal("request", true);
                    }}
                    disabled={!SHARES_PERMISSIONS.INTERNALREQUEST}
                    hidden={compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || isCms}
                >
                    <ListItemText primary={t(`common::label::Request`, `Request`)} />
                </ListItemButton>,
                <ListItemButton
                    key="starred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            false,
                        );
                    }}
                    disabled={!COLLECTIONS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => !item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Starred`, `Starred`)} />
                </ListItemButton>,
                <ListItemButton
                    key="unstarred"
                    onClick={() => {
                        onHandleClickStarred(
                            selectedItems.map((item) => item.id),
                            true,
                        );
                    }}
                    disabled={!COLLECTIONS_PERMISSIONS.READ}
                    hidden={
                        compareVersions(stage.version, VERSION_CM_14205_ASSET_STARRED) < 0 ||
                        !selectedItems.some((item) => item.myFavorite) ||
                        isCms
                    }
                >
                    <ListItemText primary={t(`common::label::Unstarred`, `Unstarred`)} />
                </ListItemButton>,
            ],
        ];
    }, [selectedItems, COLLECTIONS_PERMISSIONS]);

    const loadLinkedAssets = async (params, page) => {
        try {
            const signal = getNewController("linkedAssets");
            const response = await dispatch(getLinkedAssets({ id: collectionId, params }, signal));
            handleApiResult(response);
            if (response.nextToken) {
                const copiedTokens = [...tokenPaginationInfo.data];
                copiedTokens[page] = response.nextToken ?? null;

                setTokenPaginationInfo({ currentPage: page, data: copiedTokens });
            }
            if (isMountPending) setIsMountPending(false);
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleChangeOffset = (page) => {
        setOffset(page);
    };

    const onHandleChangeLimit = (limit) => {
        setTokenPaginationInfo((prevState) => ({ currentPage: 1, data: [] }));
        setOffset(0);
        searchQueryParams.updateSearchQueryParams({
            action: "push",
            params: { ...searchQueryParams.params, limit },
        });
    };

    const onHandleClickRefresh = () => {
        loadLinkedAssets(parsedLinkedAssetsParams, offset);
    };

    const onHandleToggleViewType = (value) => {
        if (value === isCardView) {
            return;
        }
        updateIsCardView(value);
        saveViewInfo("collectionLinkedAssetsViewInfo", {
            isCardView: !isCardView,
        });
    };

    const onHandleChangeNumSelected = (checkAll) => {
        if (checkAll) {
            setSelectedItems([].concat(filteredLinkedAssets));
        } else {
            setSelectedItems([]);
        }
    };

    const onHandleClickArchiveAsset = async (_isFolder, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });
            const ids = selectedItems?.length ? selectedItems.map((i) => i.id) : [actionTarget.id];

            const data = {
                archiveClass: option,
                targets: { ids },
            };
            const isMulti = selectedItems?.length > 1;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onHandleClickRefresh();
                setSelectedItems([]);
            });
        } catch (e) {
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onToggleModal();
        }
    };

    const handleClickRenameAsset = async (target, value) => {
        const response = await dispatch(
            updateAsset(target.id, target.version, {
                name: value,
            }),
        );
        return response;
    };

    const onHandleClickSubmitSearch = useCallback(
        (values) => {
            searchQueryParams.resetSearchQueryParams({
                action: "push",
                params: {
                    ...values,
                },
            });
        },
        [searchQueryParams],
    );

    const onHandleClickResetParams = useCallback(() => {
        searchQueryParams.resetSearchQueryParams({
            action: "replace",
            params: {},
        });
    }, []);

    const afterMutationWorks = (action) => {
        setSelectedItems([]);
        setActionTarget(null);
        onHandleClickRefresh();
    };

    const onHandleClickAddAssets = async (data, shouldUpdatePrimaryAsset) => {
        try {
            await dispatch(
                updateCollectionAction({
                    contentId: collectionId,
                    params: {
                        action: "ADD_ITEMS",
                        data,
                    },
                }),
            );
            if (shouldUpdatePrimaryAsset) {
                await dispatch(
                    updateCollection({
                        id: collectionId,
                        params: { primaryAsset: { id: data.assetIds[0] } },
                    }),
                );
            }
            onHandleClickRefresh();
            onToggleModal();
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickDeleteLinkedAsset = useCallback(
        async (ids) => {
            try {
                if (actionPending.delete) return;
                setActionPending({ ...actionPending, delete: true });

                await dispatch(
                    updateCollectionAction({
                        contentId: collection.data.id,
                        params: {
                            action: "REMOVE_ITEMS",
                            data: {
                                assetIds: ids,
                            },
                        },
                    }),
                );
                if (collection.data.primaryAsset && ids.includes(collection.data.primaryAsset.id)) {
                    await dispatch(
                        updateCollection({
                            id: collection.data.id,
                            params: { primaryAsset: { id: null } },
                        }),
                    );
                }

                onHandleClickRefresh();
                onToggleModal();
            } catch (error) {
                console.error(error);
            } finally {
                setActionPending({ ...actionPending, delete: false });
            }
        },
        [collection],
    );

    const onHandleClickSetAsDefault = useCallback(
        async (id) => {
            try {
                if (actionPending.setDefault) return;
                setActionPending({ ...actionPending, setDefault: true });

                await dispatch(
                    updateCollection({
                        id: collection.data.id,
                        params: { primaryAsset: { id } },
                    }),
                );
            } catch (error) {
                console.error(error);
            } finally {
                setActionPending({ ...actionPending, setDefault: false });
            }
        },
        [collection, actionPending],
    );

    const getLinkDestination = useCallback(
        (data) => {
            if (pathname.includes("/library")) {
                if (resourceSettingMenuName.settings?.MenuName?.selectMenu?.value === "custom")
                    return `${menuService.paths.library.customMenuSets.assetsAssetDetail(
                        stage.id,
                        project.id,
                        data.id,
                    )}`;
                else return `${menuService.paths.library.assetsAssetDetail(stage.id, project.id, data.id)}`;
            } else {
                if (resourceSettingMenuName.settings?.MenuName?.selectMenu?.value === "custom") {
                    return `${menuService.paths.customMenuSets.assetsAssetDetail(
                        stage.id,
                        project.id,
                        data.id,
                    )}?projectId=${data.project?.id}`;
                } else {
                    if (canNavigateToArchiveDetail(data.archiveStatus))
                        return menuService.paths.archiveAssetDetail(stage.id, project.id, data.id);
                    else
                        return `${menuService.paths.assetsAssetDetail(stage.id, project.id, data.id)}?projectId=${
                            project.id
                        }`;
                }
            }
        },
        [stage, project, resourceSettingMenuName],
    );

    const onHandleClickAddFolder = async ({ folderId, includeSubFolders }) => {
        try {
            await onHandleClickAddAssets({
                virtualFolder: {
                    id: folderId,
                    recursive: includeSubFolders,
                },
            });
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickAddLinkedAssets = async (selectedAssets) => {
        try {
            const shouldUpdatePrimaryAsset = !collection.data.primaryAsset && selectedAssets.length;

            await onHandleClickAddAssets(
                {
                    assetIds: selectedAssets.map((asset) => asset.id),
                },
                shouldUpdatePrimaryAsset,
            );
        } catch (error) {
            console.error(error);
        }
    };

    const onHandleClickLinkedAsset = useCallback(
        (item) => {
            setClickedItem(item);
            if (!isCms) {
                dispatch(
                    togglePropertyPanel({
                        targetId: item.id,
                        type: TABLE_LIST_TYPE.ASSET,
                    }),
                );
            }
        },
        [dispatch, isCms],
    );

    const onHandleClickStarred = useCallback(
        async (resourceIds, isStarred) => {
            const pendingId = resourceIds?.length > 1 ? "bulk" : resourceIds[0].id;
            try {
                if (actionPending[`starred-${pendingId}`]) {
                    return;
                }
                setActionPending(`starred-${pendingId}`, true);

                const data = {
                    domain: "ASSET",
                    resourceIds: resourceIds,
                };

                if (isStarred) {
                    await bulkRemoveFavoriteAPI(stage.endpoint, project.id, data);
                } else {
                    await bulkAddFavoriteAPI(stage.endpoint, project.id, data);
                }
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
                afterMutationWorks();
            } catch (error) {
                console.error(error);
                setActionPending({ ...actionPending, [`starred-${pendingId}`]: false });
            }
        },
        [actionPending],
    );

    const parsedLinkedAssetsParams = useMemo(() => {
        const {
            limit,
            name,
            id,
            tag,
            type,
            mediaType,
            ownerName,
            extension,
            createdStartAt,
            createdEndAt,
            startDuration,
            endDuration,
            status,
            jobId,
            ingestType,
            fileMeta,
        } = searchQueryParams.params;

        const params = {
            nextToken: offset > 0 ? tokenPaginationInfo.data[offset - 1] : undefined,
            limit,
            name,
            id,
            tags: tag?.join(","),
            types: type?.join(","),
            ingestType,
            mediaTypes: mediaType?.join(","),
            extensions: extension?.join(","),
            creator: ownerName,
            status,
            jobId,
            fileMeta,
        };

        if (startDuration && endDuration) {
            params.duration = `${moment.duration(startDuration).asMilliseconds()}~${
                moment.duration(endDuration).asMilliseconds() + 999
            }`;
        }

        if (createdStartAt && createdEndAt) {
            params.createdAt = `${createdStartAt}~${createdEndAt}`;
        }

        if (compareVersions(stage.version, VERSION_CM_13766_SHARES_V3) >= 0) {
            params.includeShareInfo = true;
        }

        return params;
    }, [searchQueryParams.params, offset]);

    const filteredLinkedAssets = useMemo(() => {
        if (!linkedAssets?.results?.length) return [];

        let filteredData = linkedAssets.results.map(({ createdAt, createdBy, ...item }) => ({
            ...item,
            isPreferred: collection.data.primaryAsset?.id === item.id,
            created: {
                at: createdAt,
                by: createdBy,
            },
            ...(item.owner && {
                owner: {
                    by: item.owner,
                },
            }),
        }));

        Object.entries(filterValue).map(([key, value]) => {
            if (key === "mediaType") {
                filteredData = filteredData.filter((item) => value.find(({ value }) => value === item[key]));
            }
        });

        return filteredData;
    }, [collection.data, linkedAssets, filterValue]);

    const pagination = useMemo(() => {
        return (
            <Pagination
                page={offset}
                limit={parsedLinkedAssetsParams?.limit}
                hasNextToken={tokenPaginationInfo.data[offset] !== undefined}
                totalCount={linkedAssets?.totalCount}
                onChangePage={onHandleChangeOffset}
                onChangeLimit={onHandleChangeLimit}
                onClickRefresh={onHandleClickRefresh}
            />
        );
    }, [collection, parsedLinkedAssetsParams, tokenPaginationInfo, offset]);

    const tableMenuList = useCallback(
        (item) => {
            const checkDisabledByOwnerLock = item.isOwnerLocked && item.created?.by?.id !== stage?.user?.id;

            const isExpired = item?.shareInfo?.expiresAt
                ? countDaysBetweenDates({ toDate: dayjs(item?.shareInfo?.expiresAt) }) < 0
                : false;
            const isExceedDownloadCount =
                item?.shareInfo?.maxDownloads > 0 && item?.shareInfo?.maxDownloads <= item?.shareInfo?.downloadCount;
            const hasDownloadPermissionByShare = item?.shareInfo && !(isExpired || isExceedDownloadCount);

            const hiddenDownloadButton =
                (!item?.shareInfo || !hasDownloadPermissionByShare) &&
                (!DOWNLOAD_PERMISSIONS.DOWNLOAD || (item.isOwnerLocked && item.created?.by?.id !== stage?.user?.id));

            return [
                {
                    label: t("common::label::Set as default", "Set as default"),
                    onClick: (n, value) => {
                        onHandleClickSetAsDefault(value.id);
                    },
                    clickClose: true,
                    disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                    hidden: isCms,
                },
                // {
                //     label: t("common::label::Share", "Share"),
                //     onClick: (n, value) => {
                //         onToggleModal("share", true, value);
                //     },
                //     disabled:
                //         item.archiveStatus === "ARCHIVED" ||
                //         !SHARES_PERMISSIONS.INTERNAL,
                //     divider: true,
                //     clickClose: true,
                // },
                {
                    label: t("common::label::Share link", "Share link"),
                    onClick: (n, value) => {
                        onToggleModal("shareLink", true, value);
                    },
                    divider: true,
                    disabled:
                        isArchiveRelatedStatus(item.archiveStatus) ||
                        !SHARES_PERMISSIONS.EXTERNAL ||
                        checkDisabledByOwnerLock,
                    hidden: isCms,
                    clickClose: true,
                },
                {
                    label: t("common::label::Share request", "Share request"),
                    onClick: (n, value) => {
                        onToggleModal("requestShare", true, value);
                    },
                    hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0,
                    disabled: isArchiveRelatedStatus(item.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                    clickClose: true,
                },
                {
                    label: t(`common::label::Request`, `Request`),
                    onClick: (_, value) => {
                        onToggleModal("request", true, value);
                    },
                    hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0,
                    disabled: isArchiveRelatedStatus(item.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                },
                {
                    label: t("common::label::Rename", "Rename"),
                    onClick: (n, value) => {
                        onToggleModal("rename", true, value);
                    },
                    disabled:
                        isArchiveRelatedStatus(item.archiveStatus) ||
                        !COLLECTIONS_PERMISSIONS.EDIT ||
                        checkDisabledByOwnerLock,
                    hidden: isCms,
                    divider: true,
                    clickClose: true,
                },
                {
                    label: t("common::label::Download", "Download"),
                    onClick: (n, value) => {
                        onHandleClickDownload(value);
                    },
                    disabled:
                        isArchiveRelatedStatus(item.archiveStatus) ||
                        (compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 &&
                            !DOWNLOAD_PERMISSIONS.DOWNLOAD), //Note: Archived 된 에셋은 요청해도 다운로드가 안 됨
                    hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) >= 0 && hiddenDownloadButton,
                    divider: true,
                    clickClose: true,
                },
                {
                    label: t(`common::label::Request download`, `Request download`),
                    onClick: (_, value) => {
                        onToggleModal("requestDownload", true, value);
                    },
                    hidden: compareVersions(stage.version, VERSION_CM_14143_REQUEST) < 0 || !hiddenDownloadButton,
                    disabled: isArchiveRelatedStatus(item.archiveStatus) || !SHARES_PERMISSIONS.INTERNALREQUEST,
                    clickClose: true,
                },
                {
                    label: t(
                        `common::label::${item?.myFavorite ? "Unstarred" : "Starred"}`,
                        item?.myFavorite ? "Unstarred" : "Starred",
                    ),
                    onClick: () => {
                        onHandleClickStarred([item.id], item?.myFavorite);
                    },
                    disabled: item.state === "DRAFT",
                    clickClose: true,
                },
                {
                    label: t("common::label::Remove", "Remove"),
                    onClick: (n, value) => {
                        onToggleModal("delete", true, value);
                    },
                    divider: true,
                    clickClose: true,
                    disabled: !COLLECTIONS_PERMISSIONS.EDIT,
                    hidden: isCms,
                },
            ];
        },
        [
            myProjectPermissions,
            isCms,
            onHandleClickDownload,
            collection,
            onHandleClickSetAsDefault,
            onHandleClickStarred,
        ],
    );

    const isThumbnailCropped = useMemo(() => {
        return resourceSettings.settings?.Asset?.thumbnailCropOption?.value === "true";
    }, [resourceSettings]);

    useEffect(() => {
        if (history.location.search === "") searchQueryParams.resetSearchQueryParams({ action: "push" });
        if (collection.pending || !collection.data || linkedAssets) return;

        loadLinkedAssets(parsedLinkedAssetsParams, offset);
    }, [collection]);

    useEffect(() => {
        if (isMountPending || !parsedLinkedAssetsParams.limit) return;
        loadLinkedAssets(parsedLinkedAssetsParams, offset);
    }, [parsedLinkedAssetsParams]);

    useEffect(() => {
        getSettingData("Asset");
        getSettingMenuName("MenuName", "selectMenu");
        if (compareVersions(VERSION_CM_3963_ADD_ASSET_TYPE, stage.version) <= 0) {
            dispatch(listAssetTypes());
            dispatch(listAssetExtensions());
        }
    }, []);

    useEffect(() => {
        if (!assets.assetExtensions?.data) return;

        setSearchOptionValues((prevState) => ({
            ...prevState,
            extension: assets.assetExtensions.data.map(({ extension }) => ({ value: extension, label: extension })),
        }));
    }, [assets.assetExtensions]);

    useEffect(() => {
        if (!assets.assetTypes?.data) return;

        const assetTypes = dispatch(getAssetTypeSelectList());

        setSearchOptionValues((prevState) => ({
            ...prevState,
            type: assetTypes.map(({ type }) => ({ value: type, label: type })),
        }));
    }, [assets.assetTypes]);

    return (
        <>
            {modal === "share" && (
                <DialogShare domain={"ASSET"} selectedItem={actionTarget[0]} onClickClose={() => onToggleModal()} />
            )}
            {modal === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={"ASSET"}
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onSubmit={() => {
                            onToggleModal();
                            afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
            {modal === "rename" &&
                ReactDOM.createPortal(
                    <LoadableRenameModal
                        selectedItems={actionTarget ? actionTarget : selectedItems}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={handleClickRenameAsset}
                        afterMutationWorks={afterMutationWorks}
                    />,
                    document.body,
                )}
            {modal === "archive" && (
                <DialogArchive
                    active={modal === "archive"}
                    onClose={() => onToggleModal()}
                    onSubmit={onHandleClickArchiveAsset}
                    actionPending={actionPending.archive}
                    isMulti={selectedItems.length > 1}
                />
            )}
            {modal === "addFolder" && (
                <SelectFolderPath
                    open
                    domain={"Asset"}
                    submitText={`Select`}
                    width={"600px"}
                    title={"Add assets from folder"}
                    subFoldersOption={true}
                    useNewFolder={false}
                    onSubmit={onHandleClickAddFolder}
                    onClose={() => onToggleModal()}
                />
            )}
            {modal === "requestShare" &&
                ReactDOM.createPortal(
                    <LoadableShareRequestModal
                        selectedItem={{ domain: "asset", ...actionTarget?.[0] }}
                        onClickClose={() => onToggleModal()}
                    />,
                    document.body,
                )}
            {modal === "request" && (
                <DialogRequest
                    open={modal === "request"}
                    requirePermissionCheck={false}
                    projectId={project?.id}
                    assets={actionTarget ?? selectedItems}
                    onClose={() => onToggleModal()}
                />
            )}
            {modal === "requestDownload" && (
                <DialogRequestDownload projectId={project?.id} assets={actionTarget} onClose={() => onToggleModal()} />
            )}
            {modal === "addAssets" && (
                <DialogAddLinkedAssetsV2
                    onSubmit={onHandleClickAddLinkedAssets}
                    excludeItemIds={linkedAssets?.results?.map((item) => item.id)}
                    onClose={() => onToggleModal()}
                />
            )}
            {modal === "delete" && (
                <>
                    <DialogDelete
                        items={actionTarget ?? selectedItems}
                        title={t(`common::label::Remove ${selectedItems.length > 1 ? "items" : "the item"}?`)}
                        contents={
                            <Grid maxWidth={"432px"}>
                                <Typography variant={"body1_400"} color={theme.palette.secondary[800]}>
                                    <Grid item>
                                        <span>
                                            <Trans
                                                i18nKey={`common::msg::“{{name}}”${
                                                    selectedItems.length > 1 ? ` and {{count}} others` : ""
                                                } will be removed.`}
                                                values={{
                                                    name: selectedItems[0]?.name ?? actionTarget[0].name,
                                                    count: selectedItems.length - 1,
                                                }}
                                            />
                                        </span>
                                    </Grid>
                                </Typography>
                            </Grid>
                        }
                        actionButtonTitle={"Remove"}
                        actionPending={actionPending.delete}
                        onClickClose={() => onToggleModal()}
                        onClickSubmit={() => {
                            onHandleClickDeleteLinkedAsset((actionTarget ?? selectedItems)?.map((item) => item.id));
                        }}
                    />
                </>
            )}
            <Grid id={"detail-collection-assets-view"} item height={`100%`}>
                <PageHeader
                    title={
                        <Typography variant={"subtitle1"} color={theme.palette.greySecondary.main}>
                            {t("common::label::Assets")}
                        </Typography>
                    }
                />
                <PageTools
                    search={
                        <SearchTextField
                            type={TABLE_LIST_TYPE.ASSET}
                            optionValues={searchOptionValues}
                            isSearch={false}
                            usePopover={false}
                            placeholder={t("common::msg::Please search by asset name.", "Please search by asset name.")}
                            searchQueryParams={searchQueryParams}
                            onClickSubmit={onHandleClickSubmitSearch}
                            onClickReset={onHandleClickResetParams}
                        />
                    }
                    actions={
                        <>
                            <Button
                                size="medium"
                                startIcon={<IconAdd size={20} />}
                                variant="contained"
                                onClick={(e) => {
                                    setShowCreatePopover(e.currentTarget);
                                }}
                                disabled={!collection.data || !COLLECTIONS_PERMISSIONS.EDIT}
                                hidden={isCms}
                            >
                                <Typography variant="body1_700">{t(`common::label::Add`, `Add`)}</Typography>
                            </Button>
                            <Popover
                                open={Boolean(showCreatePopover)}
                                anchorEl={showCreatePopover}
                                onClose={() => setShowCreatePopover(null)}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                }}
                                transformOrigin={{
                                    vertical: -4,
                                    horizontal: "right",
                                }}
                            >
                                <Stack
                                    direction={"column"}
                                    sx={{
                                        border: `1px solid ${theme.palette.greySecondary[100]}`,
                                        borderRadius: theme.spacing(0.5),
                                        py: theme.spacing(1),
                                        "& .MuiButton-root": {
                                            width: "200px",
                                            height: "40px",
                                            px: theme.spacing(1.75),
                                            justifyContent: "flex-start",
                                        },
                                    }}
                                >
                                    <Button
                                        color={"secondary"}
                                        variant="text"
                                        onClick={() => {
                                            onToggleModal("addAssets", true);
                                            setShowCreatePopover(null);
                                        }}
                                    >
                                        {t(`common::label::Asset`, `Asset`)}
                                    </Button>
                                    <Button
                                        color={"secondary"}
                                        variant="text"
                                        onClick={() => {
                                            onToggleModal("addFolder", true);
                                            setShowCreatePopover(null);
                                        }}
                                    >
                                        {t(`common::label::Folder`, `Folder`)}
                                    </Button>
                                </Stack>
                            </Popover>
                        </>
                    }
                />
                <ListTools
                    domain={TABLE_LIST_TYPE.COLLECTION}
                    isCardView={isCardView}
                    getButtons={listToolsButtons}
                    getPopperButtons={getListToolsPopperButtons}
                    filter={FILTER_LIST}
                    pageCount={filteredLinkedAssets?.length ?? 0}
                    numSelected={selectedItems.length}
                    hasNextToken={tokenPaginationInfo.data[tokenPaginationInfo.currentPage]}
                    onToggleViewType={onHandleToggleViewType}
                    onClickRefresh={onHandleClickRefresh}
                    onChangeNumSelected={onHandleChangeNumSelected}
                    onToggleModal={onToggleModal}
                    onChangePage={onHandleChangeOffset}
                    onChangeLimit={onHandleChangeLimit}
                    pagination={pagination}
                />

                {!linkedAssets || linkedAssets?.pending ? (
                    <>
                        {!isCardView ? (
                            <Table
                                sx={{
                                    width: "100%",
                                    borderRadius: `4px`,
                                    borderTop: `1px solid ${theme.palette.greySecondary[100]}`,
                                    borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                                }}
                            >
                                <TableBody>
                                    <TableRow>
                                        <SkeletonLinkedAssetsCollectionTable columns={LinkedAssetsColumn} />
                                    </TableRow>
                                    <TableRow>
                                        <SkeletonLinkedAssetsCollectionTable columns={LinkedAssetsColumn} />
                                    </TableRow>
                                    <TableRow>
                                        <SkeletonLinkedAssetsCollectionTable columns={LinkedAssetsColumn} />
                                    </TableRow>
                                </TableBody>
                            </Table>
                        ) : (
                            <Grid container spacing={2.5} mt={1}>
                                {new Array(16).fill("").map((_, index) => (
                                    <Grid item key={index}>
                                        <Skeleton variant="rounded" animation={"wave"} width={202} height={202} />
                                        <Skeleton
                                            variant="rounded"
                                            animation={"wave"}
                                            width={202}
                                            height={20}
                                            sx={{ marginTop: 2 }}
                                        />
                                        <Skeleton
                                            variant="rounded"
                                            animation={"wave"}
                                            width={202}
                                            height={20}
                                            sx={{ marginTop: 1 }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </>
                ) : (
                    <>
                        {!filteredLinkedAssets?.length ? (
                            <Box
                                sx={{
                                    display: `flex`,
                                    alignItems: `center`,
                                    justifyContent: `center`,
                                    width: `100%`,
                                    flex: `1`,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: `flex`,
                                        flexDirection: `column`,
                                        alignItems: `center`,
                                        justifyContent: `center`,
                                        width: `400px`,
                                        height: `400px`,
                                        textAlign: `center`,
                                    }}
                                >
                                    <ImageEmpty width={76} height={70} />
                                    <Typography
                                        variant={`subtitle2`}
                                        component={"p"}
                                        gutterBottom
                                        sx={{ marginTop: `20px` }}
                                    >
                                        {t(`common::msg::No ${"Asset"} items yet.`, `No ${"Asset"} items yet.`)}
                                    </Typography>
                                    <Typography
                                        variant={`body3bold`}
                                        component={"p"}
                                        sx={{
                                            mt: 1,
                                            color: theme.palette.secondary[600],
                                        }}
                                    >
                                        {t(
                                            `common::msg::Add it using the “Add ${"Asset"}” button.`,
                                            `Add it using the “Add ${"Asset"}” button.`,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                {isCardView ? (
                                    <ListCardView
                                        data={filteredLinkedAssets}
                                        component={
                                            <CardLinkedAsset
                                                primaryAssetId={collection.data?.primaryAsset?.id}
                                                thumbnailCrop={isThumbnailCropped}
                                                onClickDownload={(target) => onHandleClickDownload(target)}
                                                onToggleModal={onToggleModal}
                                                onClickCheck={onHandleClickCheck}
                                                onClick={onHandleClickLinkedAsset}
                                                onClickSetAsDefault={onHandleClickSetAsDefault}
                                                onClickStarred={onHandleClickStarred}
                                                onClickName={getLinkDestination}
                                            />
                                        }
                                        selected={selectedItems}
                                    />
                                ) : (
                                    <TableList
                                        keyPrefix={"linked-asset-collection"}
                                        className={"detail-collections-table-list"}
                                        type={"asset"}
                                        checkable
                                        columns={
                                            compareVersions(
                                                stage.version,
                                                VERSION_CM_14830_SEPARATION_OF_OWNER_AND_CREATOR,
                                            ) < 0
                                                ? LinkedAssetsColumn
                                                : LinkedAssetsColumnV2
                                        }
                                        data={filteredLinkedAssets}
                                        selected={selectedItems}
                                        clicked={clickedItem}
                                        thumbnailCrop={isThumbnailCropped}
                                        disableHover={false}
                                        onClickName={getLinkDestination}
                                        onClickCheck={onHandleClickCheck}
                                        onClickRow={onHandleClickLinkedAsset}
                                        actionButton={(item) => (
                                            <Menu
                                                menuMinWidth={160}
                                                placement={"right"}
                                                type={"icon"}
                                                label={<IconMoreHoriz size={16} />}
                                                color={"inherit"}
                                                size={"medium"}
                                                value={item}
                                                list={tableMenuList(item)}
                                            />
                                        )}
                                        showAdditionalIcons={true}
                                        showColumns={true}
                                    />
                                )}
                            </>
                        )}
                    </>
                )}
            </Grid>
        </>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        offset: yup.number().default(0),
        isQuickSearch: yup.bool().default(true),
    },
    setProps: true,
    // removeExtraParams: true,
})(LinkedAssetsDetailCollection);
